import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import logger from '../utils/logger';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Initialize language from localStorage immediately
  const getInitialLanguage = () => {
    const storedLanguage = localStorage.getItem('preferred_language');
    const browserLanguage = navigator.language?.split('-')[0];
    
    if (storedLanguage && ['es', 'en'].includes(storedLanguage)) {
      return storedLanguage;
    } else if (['es', 'en'].includes(browserLanguage)) {
      return browserLanguage;
    }
    return 'es'; // Default to Spanish instead of English
  };
  
  const [language, setLanguage] = useState(getInitialLanguage());
  const [content, setContent] = useState(null);
  const [metaCapitals, setMetaCapitals] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load language content
  useEffect(() => {
    const loadLanguageContent = async () => {
      try {
        setLoading(true);
        
        // Load UI strings and content
        const contentResponse = await axios.get(`/languages/${language}/content`);
        if (contentResponse.data.success) {
          setContent(contentResponse.data.data.ui_strings);
          setMetaCapitals(contentResponse.data.data.meta_capitals);
        }
        
      } catch (error) {
        logger.error('[LanguageContext] Error loading language content:', error);
        // Fallback content
        setContent({
          welcome: language === 'es' ? 'Bienvenido' : 'Welcome',
          loading: language === 'es' ? 'Cargando...' : 'Loading...'
        });
      } finally {
        setLoading(false);
      }
    };

    loadLanguageContent();
  }, [language]);

  const changeLanguage = (newLanguage) => {
    if (newLanguage !== language && ['es', 'en'].includes(newLanguage)) {
      setLanguage(newLanguage);
      localStorage.setItem('preferred_language', newLanguage);
    }
  };

  const t = (key, defaultValue = '') => {
    if (!content) return defaultValue;
    
    // Handle nested keys like 'buttons.start'
    const keys = key.split('.');
    let value = content;
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }
    
    return value || defaultValue || key;
  };

  const value = {
    language,
    content,
    metaCapitals,
    loading,
    changeLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};